import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import QuickEntryFormModal from '@/components/feature/QuickEntryFormModal';
import { doctors, hospitals } from '@/mocks/home';
import DoctorCard from '@/components/feature/DoctorCard';
import { fetchHospitalInfo } from '@/services/hospital';
import { fetchDoctorList } from '@/services/doctor';
import { adaptDoctors, adaptHospital } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';
import type { DoctorItem, HospitalItem } from '@/types/content';

const specialties = ['中西医结合内科', '中医外科', '骨伤科', '妇科', '儿科', '针灸推拿科'];

export default function HospitalDetailPage() {
  const { id } = useParams();
  const hospitalId = Number(id);
  /** 申请机构合作弹窗（与生态合作表单共用） */
  const [cooperationOpen, setCooperationOpen] = useState(false);

  // 详情接口失败时降级为 mock 数据
  const { data: hospital, loading } = useRequest<HospitalItem | undefined>(
    () => fetchHospitalInfo(hospitalId).then((res) => adaptHospital(res.dataInfo)),
    { fallback: hospitals.find((item) => item.id === hospitalId), deps: [hospitalId] },
  );

  // 医院医师需从医师列表匹配（详情接口仅返回姓名与职称）
  const { data: doctorList } = useRequest<DoctorItem[]>(
    () => fetchDoctorList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptDoctors(res.list)),
    { fallback: doctors },
  );

  if (loading && !hospital) {
    return (
      <PageShell>
        <PageHero
          eyebrow="医院专科"
          title="正在加载医院信息"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '医院专科', to: '/hospitals' }, { label: '医院详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-24 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </section>
      </PageShell>
    );
  }

  if (!hospital) {
    return (
      <PageShell>
        <PageHero
          eyebrow="医院专科"
          title="医院信息不存在"
          description="抱歉，未找到该医院信息，可能链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '医院专科', to: '/hospitals' }, { label: '医院详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/hospitals"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回医院专科
          </Link>
        </section>
      </PageShell>
    );
  }

  // 医师优先关联医师库（可跳转主页）；医师库暂无该医师时，回退展示医院提供的姓名与职称（不可跳转）
  const hospitalDoctors = hospital.doctors.map((item) => {
    const matched = doctorList.find((doc) => doc.name === item.name);
    if (matched) return { doctor: matched, linkable: true };
    const fallback: DoctorItem = {
      id: 0,
      name: item.name,
      title: item.title,
      hospital: hospital.name,
      tags: [],
      intro: '',
      avatar: '',
    };
    return { doctor: fallback, linkable: false };
  });

  // 后台「医师管理」中将医院单位关联到该医院的医师，自动补充展示（医师关联医院 → 官网医院详情同步，形成闭环）
  const configuredNames = new Set(hospital.doctors.map((item) => item.name));
  const linkedDoctors = doctorList
    .filter((doc) => doc.hospital === hospital.name && !configuredNames.has(doc.name))
    .map((doc) => ({ doctor: doc, linkable: true }));

  const allHospitalDoctors = [...hospitalDoctors, ...linkedDoctors];

  return (
    <PageShell>
      <PageHero
        eyebrow="医院专科"
        title={hospital.name}
        breadcrumb={[{ label: '首页', to: '/' }, { label: '医院专科', to: '/hospitals' }, { label: '医院详情' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="w-full h-[260px] md:h-[420px] rounded-lg overflow-hidden border border-background-200">
            <img
              src={hospital.image}
              alt={hospital.name}
              title={`${hospital.name} - 合作医院`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="lg:col-span-2 flex flex-col gap-10 md:gap-12">
              <div>
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                  <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                  医院简介
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{hospital.intro}</p>
              </div>

              {hospital.campusGallery.length > 0 && (
                <div>
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                    <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                    院区环境
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">
                    从门诊、住院到康养，实景呈现院区诊疗与养护环境
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {hospital.campusGallery.map((item) => (
                      <figure
                        key={item.label}
                        className="group relative w-full h-[200px] md:h-[220px] rounded-lg overflow-hidden border border-background-200"
                      >
                        <img
                          src={item.image}
                          alt={`${hospital.name} ${item.label}`}
                          title={`${hospital.name} - ${item.label}实景`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
                        <figcaption className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950/50 backdrop-blur text-xs font-medium text-background-50">
                          {item.label}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              )}

              {hospital.advantageDiseases.length > 0 && (
                <div>
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                    <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                    优势病种
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">
                    共 {hospital.advantageDiseases.length} 个特色诊疗方案，点击卡片查看病种详情
                  </p>
                  <div className="mt-4 flex flex-col gap-4">
                    {hospital.advantageDiseases.map((item) => (
                      <Link
                        key={item.name}
                        to={`/diseases/${item.diseaseId}`}
                        className="group flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200 hover:border-accent-300 hover:bg-accent-50/60 transition-colors cursor-pointer"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                          <i className="ri-heart-pulse-line"></i>
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading font-semibold text-foreground-900 leading-snug group-hover:text-accent-700 transition-colors">
                            {item.name}
                          </h3>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                          <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-accent-700">
                            查看病种详情
                            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {hospital.preparations.length > 0 && (
                <div>
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                    <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                    医院制剂
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">
                    共 {hospital.preparations.length} 种院内特色制剂
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {hospital.preparations.map((item) => (
                      <div
                        key={item.name}
                        className="flex flex-col p-5 rounded-lg bg-background-50 border border-background-200"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                            <i className="ri-medicine-bottle-line"></i>
                          </span>
                          <h3 className="font-heading font-semibold text-foreground-900 leading-snug">{item.name}</h3>
                        </div>
                        <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                  <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                  特色专科
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {specialties.map((item) => (
                    <span
                      key={item}
                      className="px-3.5 py-2 rounded-full bg-accent-50 border border-accent-100 text-accent-800 text-sm"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {allHospitalDoctors.length > 0 && (
                <div>
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                    <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                    医院医师
                  </h2>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {allHospitalDoctors.map(({ doctor, linkable }) => (
                      <DoctorCard key={doctor.name} doctor={doctor} linkable={linkable} showAction={linkable} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4">机构概览</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col items-center justify-center rounded-md bg-background-100 border border-background-200 py-4">
                    <span className="font-heading text-2xl font-bold text-primary-600">
                      {hospital.advantageDiseases.length}
                    </span>
                    <span className="mt-1 text-xs text-foreground-500">优势病种</span>
                  </div>
                  <div className="flex flex-col items-center justify-center rounded-md bg-background-100 border border-background-200 py-4">
                    <span className="font-heading text-2xl font-bold text-accent-600">
                      {hospital.preparations.length}
                    </span>
                    <span className="mt-1 text-xs text-foreground-500">医院制剂</span>
                  </div>
                  <div className="flex flex-col items-center justify-center rounded-md bg-background-100 border border-background-200 py-4 col-span-2">
                    <span className="font-heading text-2xl font-bold text-secondary-600">
                      {allHospitalDoctors.length}
                    </span>
                    <span className="mt-1 text-xs text-foreground-500">合作医师</span>
                  </div>
                </div>

                <a
                  href={hospital.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  访问医院官网
                </a>
              </div>
            </aside>
          </div>

          <div className="mt-10 md:mt-14 rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="flex items-start gap-4 flex-1">
                <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                  <i className="ri-hand-heart-line text-2xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900">申请机构合作</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                    医院、基层医疗机构与产业伙伴均可申请入驻，共建课程、活动、医师与数据资源体系，共享中西医结合智慧生态。
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCooperationOpen(true)}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hand-heart-line"></i>
                立即申请合作
              </button>
            </div>
          </div>
        </div>
      </section>

      <QuickEntryFormModal
        type={cooperationOpen ? 'cooperation' : null}
        onClose={() => setCooperationOpen(false)}
      />
    </PageShell>
  );
}